import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import CourseCard from "../components/CourseCard";
import FAQSection from "../components/FAQSection";
import DisciplineCategoriesStrip from "../components/DisciplineCategoriesStrip";
import FeaturedCoaches from "../components/FeaturedCoaches";
import { WeekView, DayView } from "../components/AgendaCalendarViews";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRight, MagnifyingGlass, Lightning, ShieldCheck, CurrencyCircleDollar, MagnifyingGlassPlus, CreditCard, Star, UserPlus, Megaphone, Coins, Barbell } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getDisciplineCategories, getCategoryDisciplineIds } from "../lib/disciplines";

function renderCourses(loading, filtered, t) {
  if (loading) {
    return <div className="text-center py-20 text-xs uppercase tracking-[0.3em] text-zinc-500">{t("landing.loading_courses")}</div>;
  }
  if (filtered.length === 0) {
    return (
      <div className="border border-white/10 p-12 text-center bg-[#0A0A0A]">
        <div className="font-display text-2xl uppercase text-zinc-400">{t("landing.no_courses")}</div>
        <div className="text-xs text-zinc-500 mt-2 uppercase tracking-wider">{t("landing.no_courses_hint")}</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 fade-up-stagger" data-testid="course-grid">
      {filtered.map((c, i) => <CourseCard key={c.id} course={c} index={i} />)}
    </div>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "it";
  const DISCIPLINE_CATEGORIES = useMemo(() => getDisciplineCategories(lang), [lang]);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [discipline, setDiscipline] = useState("Tutti");
  const [location, setLocation] = useState("Tutti");
  const [activeCategory, setActiveCategory] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // list | week | day

  const handleSelectCategory = useCallback((key) => {
    setActiveCategory(key);
    if (key) setDiscipline("Tutti");
    if (key && typeof window !== "undefined") {
      setTimeout(() => {
        document.getElementById("agenda")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/courses", { params: { status: "approved" } });
      setCourses(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const locations = useMemo(() => {
    const s = new Set(courses.map((c) => c.location));
    return ["Tutti", ...Array.from(s)];
  }, [courses]);

  const filtered = useMemo(() => {
    const categoryIds = activeCategory ? getCategoryDisciplineIds(activeCategory) : null;
    return courses.filter((c) => {
      if (search && !`${c.title} ${c.instructor_name} ${c.venue}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (categoryIds && !categoryIds.includes(c.discipline)) return false;
      if (!categoryIds && discipline !== "Tutti" && c.discipline !== discipline) return false;
      if (location !== "Tutti" && c.location !== location) return false;
      return true;
    });
  }, [courses, search, discipline, activeCategory, location]);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000')",
            filter: "grayscale(1) contrast(1.1)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/85 to-black/60" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-36">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#E30613]/60 px-3 py-1 mb-6">
              <Lightning size={12} weight="fill" className="text-[#E30613]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("landing.tag")}</span>
            </div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase font-black leading-[0.9] tracking-tighter">
              {t("landing.hero_line1")}<br/>
              <span className="text-[#E30613]">{t("landing.hero_line2")}</span><br/>
              {t("landing.hero_line3")}
            </h1>
            <p className="mt-8 text-zinc-300 text-base max-w-xl leading-relaxed">
              <Trans i18nKey="landing.hero_subtitle" components={{ b: <strong className="text-white font-bold" /> }} />
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-register-cta">
                <Button className="bg-[#E30613] hover:bg-[#FF1A27] text-white text-xs uppercase tracking-[0.2em] font-bold rounded-none px-8 py-6 btn-tactical">
                  {t("landing.cta_start")} <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
              <a href="#agenda" data-testid="hero-browse-cta">
                <Button variant="outline" className="border-white/30 hover:bg-white/5 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-none px-8 py-6">
                  {t("landing.cta_browse")}
                </Button>
              </a>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-8 max-w-xl">
              <div>
                <div className="font-display text-4xl font-black text-white">{courses.length}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">{t("landing.stat_active_courses")}</div>
              </div>
              <div>
                <div className="font-display text-4xl font-black text-white">{locations.length - 1}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">{t("landing.stat_cities")}</div>
              </div>
              <div>
                <div className="font-display text-4xl font-black text-[#E30613]">TWINT</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">{t("landing.stat_payment")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { key: "trust-verified", icon: ShieldCheck, l: "trust_verified_title", d: "trust_verified_desc" },
            { key: "trust-payment", icon: CurrencyCircleDollar, l: "trust_payment_title", d: "trust_payment_desc" },
            { key: "trust-instant", icon: Lightning, l: "trust_instant_title", d: "trust_instant_desc" },
          ].map((it) => (
            <div key={it.key} className="flex items-start gap-3" data-testid={it.key}>
              <it.icon size={28} className="text-[#E30613] shrink-0" weight="duotone" />
              <div>
                <div className="text-sm font-bold uppercase tracking-wider text-white">{t(`landing.${it.l}`)}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{t(`landing.${it.d}`)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DISCIPLINE CATEGORIES STRIP */}
      <DisciplineCategoriesStrip
        activeCategory={activeCategory}
        onSelectCategory={handleSelectCategory}
      />

      {/* FEATURED COACHES */}
      <FeaturedCoaches courses={courses} />

      {/* HOW IT WORKS */}
      <section id="come-funziona" className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(rgba(227,6,19,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(227,6,19,0.4) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("landing.how_tag")}</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase font-black text-white mt-3 tracking-tight leading-[0.95]">
              {t("landing.how_title")}
            </h2>
            <p className="mt-5 text-zinc-400 text-base leading-relaxed">{t("landing.how_subtitle")}</p>
          </div>

          {/* Client Track */}
          <div className="mt-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px bg-[#E30613]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("landing.how_client_tag")}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { num: "01", icon: MagnifyingGlassPlus, key: "how_client_1" },
                { num: "02", icon: CreditCard, key: "how_client_2" },
                { num: "03", icon: Star, key: "how_client_3" },
              ].map((step) => (
                <div key={step.key} className="relative border border-white/10 bg-[#0A0A0A] p-6 hover:border-[#E30613]/60 transition-colors" data-testid={`how-${step.key}`}>
                  <div className="font-display text-7xl font-black text-[#E30613]/10 absolute top-2 right-4 leading-none select-none">{step.num}</div>
                  <step.icon size={32} className="text-[#E30613] relative" weight="duotone" />
                  <h3 className="font-display text-xl uppercase font-black text-white mt-5 relative">
                    {t(`landing.${step.key}_title`)}
                  </h3>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed relative">
                    {t(`landing.${step.key}_desc`)}
                  </p>
                </div>
              ))}
            </div>
            <Link to="/register" className="inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-[0.2em] text-white hover:text-[#E30613] font-bold transition-colors" data-testid="how-cta-client">
              {t("landing.how_cta_client")} <ArrowRight size={14} />
            </Link>
          </div>

          {/* Instructor Track */}
          <div className="mt-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px bg-[#E30613]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("landing.how_instructor_tag")}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { num: "01", icon: UserPlus, key: "how_instructor_1" },
                { num: "02", icon: Megaphone, key: "how_instructor_2" },
                { num: "03", icon: Coins, key: "how_instructor_3" },
              ].map((step) => (
                <div key={step.key} className="relative border border-white/10 bg-[#0A0A0A] p-6 hover:border-[#E30613]/60 transition-colors" data-testid={`how-${step.key}`}>
                  <div className="font-display text-7xl font-black text-[#E30613]/10 absolute top-2 right-4 leading-none select-none">{step.num}</div>
                  <step.icon size={32} className="text-[#E30613] relative" weight="duotone" />
                  <h3 className="font-display text-xl uppercase font-black text-white mt-5 relative">
                    {t(`landing.${step.key}_title`)}
                  </h3>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed relative">
                    {t(`landing.${step.key}_desc`)}
                  </p>
                </div>
              ))}
            </div>
            <Link to="/register" className="inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-[0.2em] text-white hover:text-[#E30613] font-bold transition-colors" data-testid="how-cta-instructor">
              <Barbell size={14} /> {t("landing.how_cta_instructor")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* AGENDA */}
      <section id="agenda" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("landing.agenda_tag")}</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase font-black text-white mt-2 tracking-tight">
              {t("landing.agenda_title")}
            </h2>
          </div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider">
            {filtered.length} {t("common.results")}
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 border border-white/10 p-4 bg-[#0A0A0A]">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("landing.search_placeholder")}
              className="pl-9 bg-black border-white/20 rounded-none focus-visible:ring-[#E30613] focus-visible:border-[#E30613] h-11 text-sm"
              data-testid="agenda-search-input"
            />
          </div>
          <Select value={discipline} onValueChange={setDiscipline}>
            <SelectTrigger className="bg-black border-white/20 rounded-none h-11 text-sm" data-testid="agenda-discipline-select">
              <SelectValue placeholder={t("landing.discipline_placeholder")} />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/20 rounded-none max-h-[60vh]">
              <SelectItem value="Tutti" className="text-sm">{t("common.all_disciplines")}</SelectItem>
              {DISCIPLINE_CATEGORIES.map(([cat, items]) => (
                <React.Fragment key={cat}>
                  <div className="px-2 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-[#E30613] font-bold">{cat}</div>
                  {items.map((d) => (
                    <SelectItem key={d.id} value={d.id} className="text-sm">{d.displayLabel}</SelectItem>
                  ))}
                </React.Fragment>
              ))}
            </SelectContent>
          </Select>
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="bg-black border-white/20 rounded-none h-11 text-sm" data-testid="agenda-location-select">
              <SelectValue placeholder={t("landing.location_placeholder")} />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/20 rounded-none">
              {locations.map((l) => (<SelectItem key={l} value={l} className="text-sm">{l === "Tutti" ? t("common.all_locations") : l}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 mb-6" data-testid="agenda-view-toggle">
          {[
            { k: "list", label: t("agenda.view_list") },
            { k: "week", label: t("agenda.view_week") },
            { k: "day", label: t("agenda.view_day") },
          ].map((v) => (
            <button
              key={v.k}
              onClick={() => setViewMode(v.k)}
              className={`h-9 px-4 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${
                viewMode === v.k
                  ? "bg-[#E30613] border-[#E30613] text-white"
                  : "bg-transparent border-white/20 text-zinc-400 hover:border-[#E30613]/60 hover:text-white"
              }`}
              data-testid={`agenda-view-${v.k}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {viewMode === "list" && renderCourses(loading, filtered, t)}
        {viewMode === "week" && !loading && <WeekView courses={filtered} lang={lang} t={t} />}
        {viewMode === "day" && !loading && <DayView courses={filtered} lang={lang} t={t} />}
      </section>

      {/* FAQ */}
      <FAQSection />

      <footer className="border-t border-white/10 mt-0 py-12 px-6 lg:px-10 bg-[#050505]" data-testid="site-footer">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-2">
            <div className="flex items-center gap-2">
              <Lightning size={20} weight="fill" className="text-[#E30613]" />
              <span className="font-display text-lg uppercase font-black">FitAgenda <span className="text-[#E30613]">CH</span></span>
            </div>
            <p className="mt-4 text-xs text-zinc-500 leading-relaxed max-w-md">{t("landing.footer_disclaimer")}</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold mb-4">{t("landing.footer_legal")}</div>
            <ul className="space-y-2 text-xs text-zinc-400">
              <li><Link to="/legal/termini" className="hover:text-white" data-testid="footer-link-terms">{t("landing.footer_terms")}</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-white" data-testid="footer-link-privacy">{t("landing.footer_privacy")}</Link></li>
              <li><Link to="/legal/impressum" className="hover:text-white" data-testid="footer-link-impressum">{t("landing.footer_impressum")}</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-white" data-testid="footer-link-cookies">{t("landing.footer_cookies")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold mb-4">{t("landing.footer_contact")}</div>
            <ul className="space-y-2 text-xs text-zinc-400">
              <li><a href="#faq" className="hover:text-white" data-testid="footer-link-faq">{t("landing.footer_faq")}</a></li>
              <li><a href="mailto:info@fitagenda.ch" className="hover:text-white">info@fitagenda.ch</a></li>
              <li><a href="mailto:privacy@fitagenda.ch" className="hover:text-white">privacy@fitagenda.ch</a></li>
              <li><a href="mailto:support@fitagenda.ch" className="hover:text-white">support@fitagenda.ch</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/5 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          {t("landing.footer_copy")}
        </div>
      </footer>
    </div>
  );
}
